/**
 * TabletWizard v2 — Vor-Ort-Signatur Wizard für Marple Tablet
 * Routing: handover → Native Canvas | lease → DocuSeal
 * 4 Schritte: Objekt → Dokument → Parteien → Signatur/Absenden
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Building2, FileText, PenLine, CheckCircle,
  ChevronRight, ChevronLeft, Loader2, ShieldCheck,
  User, Mail, Smartphone, AlertCircle, RefreshCw,
  Sparkles, Eye, EyeOff, FlaskConical, Trash2,
  MapPin, Clock, Fingerprint, Send
} from 'lucide-react';
import { propertiesService } from '../services/propertiesService';

const API_BASE = '/api/control';

// ── Schritt-Indikatoren ───────────────────────────────────────────────────────
const StepIndicator = ({ steps, current }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {steps.map((step, i) => (
      <React.Fragment key={i}>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
          i === current
            ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
            : i < current
              ? 'bg-green-600/20 text-green-400 border border-green-600/30'
              : 'bg-gray-700/50 text-gray-500 border border-gray-600/30'
        }`}>
          {i < current
            ? <CheckCircle className="w-4 h-4" />
            : <span className="w-4 h-4 flex items-center justify-center text-xs">{i + 1}</span>
          }
          <span className="hidden sm:inline">{step}</span>
        </div>
        {i < steps.length - 1 && (
          <ChevronRight className={`w-4 h-4 flex-shrink-0 ${i < current ? 'text-green-400' : 'text-gray-600'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ── Schritt 1: Objekt wählen ──────────────────────────────────────────────────
const StepObjekt = ({ onSelect, selected }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    propertiesService.getProperties()
      .then(data => setProperties(data || []))
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = properties.filter(p =>
    !search || (p.street_address || p.name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Objekt auswählen</h2>
        <p className="text-gray-400 text-sm">Für welches Objekt soll ein Dokument erstellt werden?</p>
      </div>
      <input
        type="text"
        placeholder="Suche nach Adresse…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 text-base focus:outline-none focus:border-blue-500"
      />
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
          {filtered.map(p => (
            <button
              key={p.id}
              onClick={() => onSelect(p)}
              className={`w-full text-left px-4 py-4 rounded-xl border transition-all flex items-center gap-3 ${
                selected?.id === p.id
                  ? 'bg-blue-600/20 border-blue-500 text-white'
                  : 'bg-gray-700/50 border-gray-600/50 text-gray-200 hover:bg-gray-700 hover:border-gray-500 active:scale-[0.98]'
              }`}
            >
              <Building2 className={`w-5 h-5 flex-shrink-0 ${selected?.id === p.id ? 'text-blue-400' : 'text-gray-400'}`} />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{p.street_address || p.name || p.id}</p>
                {p.city && <p className="text-xs text-gray-400 mt-0.5">{p.postal_code} {p.city}</p>}
              </div>
              {selected?.id === p.id && <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-gray-500 py-8">Keine Objekte gefunden</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Schritt 2: Template wählen ────────────────────────────────────────────────
const CATEGORY_LABELS = {
  handover: 'Übergabe',
  lease: 'Mietvertrag',
  loan: 'Darlehen',
  other: 'Sonstiges'
};

const ADAPTER_BADGE = {
  native_canvas: { label: 'Vor-Ort', color: 'text-green-400 bg-green-900/20 border-green-700/40' },
  docuseal: { label: 'E-Mail', color: 'text-blue-400 bg-blue-900/20 border-blue-700/40' }
};

const StepTemplate = ({ onSelect, selected }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/signature/templates`)
      .then(r => r.json())
      .then(data => setTemplates(data.templates || data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Dokumententyp</h2>
        <p className="text-gray-400 text-sm">Welches Dokument soll erstellt und unterzeichnet werden?</p>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Übergabe zuerst */}
          {['handover', 'lease', 'loan', 'other'].filter(c => grouped[c]).map(cat => (
            <div key={cat}>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 px-1">
                {CATEGORY_LABELS[cat] || cat}
              </p>
              <div className="space-y-2">
                {grouped[cat].map(t => {
                  const adapter = t.policy?.default_adapter || (cat === 'handover' ? 'native_canvas' : 'docuseal');
                  const badge = ADAPTER_BADGE[adapter] || ADAPTER_BADGE.docuseal;
                  return (
                    <button
                      key={t.id}
                      onClick={() => onSelect(t)}
                      className={`w-full text-left px-4 py-4 rounded-xl border transition-all flex items-center gap-3 ${
                        selected?.id === t.id
                          ? 'bg-blue-600/20 border-blue-500 text-white'
                          : 'bg-gray-700/50 border-gray-600/50 text-gray-200 hover:bg-gray-700 hover:border-gray-500 active:scale-[0.98]'
                      }`}
                    >
                      <FileText className={`w-5 h-5 flex-shrink-0 ${selected?.id === t.id ? 'text-blue-400' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{t.label || t.name}</p>
                        {t.submitter_roles && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {t.submitter_roles.join(' · ')}
                          </p>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${badge.color}`}>
                        {badge.label}
                      </span>
                      {selected?.id === t.id && <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Schritt 3: Daten + KI-Autofill ───────────────────────────────────────────
const StepDaten = ({ property, template, onData, initialData }) => {
  const [ownerData, setOwnerData] = useState(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [form, setForm] = useState(initialData || {
    mieter_name: '',
    mieter_email: '',
    mieter_telefon: '',
    vermieter_name: '',
    vermieter_email: '',
  });
  const [showOwner, setShowOwner] = useState(false);
  const isHandover = template?.category === 'handover';

  useEffect(() => {
    if (!property?.id) return;
    setOwnerLoading(true);
    fetch(`${API_BASE}/signature/owner/${property.id}`)
      .then(r => r.json())
      .then(data => {
        setOwnerData(data);
        if (data?.owner) {
          setForm(prev => ({
            ...prev,
            vermieter_name: data.owner.vermieter_name || prev.vermieter_name,
            vermieter_email: data.owner.vermieter_email || prev.vermieter_email,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setOwnerLoading(false));
  }, [property?.id]);

  const update = (k, v) => {
    const next = { ...form, [k]: v };
    setForm(next);
    onData(next);
  };

  const Field = ({ label, field, type = 'text', icon: Icon, placeholder, required }) => (
    <div>
      <label className="block text-xs text-gray-400 mb-1.5 font-medium">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />}
        <input
          type={type}
          value={form[field] || ''}
          onChange={e => update(field, e.target.value)}
          placeholder={placeholder}
          className={`w-full bg-gray-700 border border-gray-600 rounded-xl py-3 text-white placeholder-gray-500 text-base focus:outline-none focus:border-blue-500 transition-colors ${Icon ? 'pl-10 pr-4' : 'px-4'}`}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Beteiligte Parteien</h2>
        <p className="text-gray-400 text-sm">
          {isHandover
            ? 'Vermieter-Daten automatisch geladen. Mieter-Daten ergänzen.'
            : 'Vermieter-Daten automatisch geladen. Mieter-E-Mail für Signaturlink angeben.'}
        </p>
      </div>

      {/* Mieter */}
      <div className="bg-gray-700/30 rounded-xl p-4 space-y-3 border border-gray-600/30">
        <p className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <User className="w-4 h-4 text-blue-400" /> Mieter
        </p>
        <Field label="Name / Firma" field="mieter_name" icon={User} placeholder="Max Mustermann" required />
        {!isHandover && (
          <Field label="E-Mail" field="mieter_email" type="email" icon={Mail} placeholder="mieter@beispiel.de" required />
        )}
        <Field label="Telefon" field="mieter_telefon" type="tel" icon={Smartphone} placeholder="+49 621 …" />
      </div>

      {/* Vermieter (Autofill) */}
      <div className={`rounded-xl p-4 space-y-3 border ${
        ownerData?.complete
          ? 'bg-green-900/10 border-green-700/40'
          : 'bg-gray-700/30 border-gray-600/30'
      }`}>
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-300 flex items-center gap-2">
            {ownerData?.complete
              ? <ShieldCheck className="w-4 h-4 text-green-400" />
              : <AlertCircle className="w-4 h-4 text-yellow-400" />
            }
            Vermieter
            {ownerLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
            {ownerData?.complete && (
              <span className="text-xs text-green-400 font-normal">automatisch befüllt</span>
            )}
          </p>
          <button
            onClick={() => setShowOwner(!showOwner)}
            className="text-xs text-gray-400 hover:text-gray-300 flex items-center gap-1"
          >
            {showOwner ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showOwner ? 'Ausblenden' : 'Details'}
          </button>
        </div>

        {showOwner && (
          <div className="space-y-3">
            <Field label="Name / Firma" field="vermieter_name" icon={User} placeholder="Vermieter GmbH" />
            {!isHandover && (
              <Field label="E-Mail" field="vermieter_email" type="email" icon={Mail} placeholder="vermieter@beispiel.de" />
            )}
          </div>
        )}

        {!showOwner && ownerData?.owner && (
          <p className="text-sm text-gray-300">
            {ownerData.owner.vermieter_name}
            {!isHandover && ownerData.owner.vermieter_email && (
              <span className="text-gray-500 text-xs ml-2">· {ownerData.owner.vermieter_email}</span>
            )}
          </p>
        )}

        {ownerData?.missing_fields?.length > 0 && (
          <p className="text-xs text-yellow-400">
            Fehlend: {ownerData.missing_fields.join(', ')}
          </p>
        )}
      </div>

      {/* Ort (für Übergabe) */}
      {isHandover && (
        <div>
          <label className="block text-xs text-gray-400 mb-1.5 font-medium">Ort der Übergabe</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={form.ort || ''}
              onChange={e => update('ort', e.target.value)}
              placeholder={property?.street_address || 'Übergabeort'}
              className="w-full bg-gray-700 border border-gray-600 rounded-xl py-3 pl-10 pr-4 text-white placeholder-gray-500 text-base focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Signature Pad Komponente ──────────────────────────────────────────────────
const SignaturePad = ({ label, name, onSignature, onName, nameValue, required }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const lastPos = useRef(null);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    setIsDrawing(true);
    lastPos.current = getPos(e, canvas);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
    setHasSig(true);
  };

  const stopDraw = (e) => {
    e?.preventDefault();
    setIsDrawing(false);
    if (hasSig) exportSig();
  };

  const exportSig = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Als SVG exportieren (Pfad aus Canvas-Daten)
    const dataUrl = canvas.toDataURL('image/png');
    // Wir senden das als SVG-Wrapper mit eingebettetem PNG
    const w = canvas.width, h = canvas.height;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><image href="${dataUrl}" width="${w}" height="${h}"/></svg>`;
    onSignature(svg);
  }, [hasSig, onSignature]);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
    onSignature(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <Fingerprint className="w-4 h-4 text-blue-400" />
          {label}
          {required && <span className="text-red-400">*</span>}
          {hasSig && <CheckCircle className="w-4 h-4 text-green-400" />}
        </label>
        {hasSig && (
          <button
            onClick={clear}
            className="text-xs text-gray-400 hover:text-red-400 flex items-center gap-1 transition-colors"
          >
            <Trash2 className="w-3 h-3" /> Löschen
          </button>
        )}
      </div>

      {/* Name in Klarschrift */}
      <input
        type="text"
        value={nameValue || ''}
        onChange={e => onName(e.target.value)}
        placeholder={`Name in Klarschrift (${label})`}
        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-500"
      />

      {/* Canvas */}
      <div className={`relative rounded-xl border-2 overflow-hidden transition-colors ${
        hasSig ? 'border-green-600/60' : 'border-gray-600 border-dashed'
      }`} style={{ background: '#1a1f2e' }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={180}
          className="w-full touch-none cursor-crosshair"
          style={{ display: 'block' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
        {!hasSig && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-gray-600 text-sm">Hier unterschreiben</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Schritt 4a: Canvas-Signatur (für Übergabe) ────────────────────────────────
const StepCanvasSignatur = ({ property, template, formData, onComplete, submitting }) => {
  const [tenantSig, setTenantSig] = useState(null);
  const [ownerSig, setOwnerSig] = useState(null);
  const [tenantName, setTenantName] = useState(formData?.mieter_name || '');
  const [ownerName, setOwnerName] = useState(formData?.vermieter_name || '');

  const canSubmit = tenantSig && ownerSig && tenantName && ownerName && !submitting;

  const handleComplete = () => {
    onComplete({
      tenant_signature_svg: tenantSig,
      owner_signature_svg: ownerSig,
      signed_by_tenant_name: tenantName,
      signed_by_owner_name: ownerName,
      location: formData?.ort || property?.street_address || '',
      signed_at: new Date().toISOString()
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Vor-Ort-Signatur</h2>
        <p className="text-gray-400 text-sm">
          Beide Parteien unterschreiben direkt auf dem Tablet.
          Das Dokument wird sofort im Vault gespeichert.
        </p>
      </div>

      <div className="bg-gray-700/20 rounded-xl p-3 border border-gray-600/30 flex items-center gap-3">
        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="text-xs text-gray-400">
          <span className="text-gray-300 font-medium">{template?.label}</span>
          {' · '}
          {property?.street_address}
          {' · '}
          {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </div>
      </div>

      <SignaturePad
        label="Unterschrift Mieter"
        nameValue={tenantName}
        onName={setTenantName}
        onSignature={setTenantSig}
        required
      />

      <div className="border-t border-gray-700/50" />

      <SignaturePad
        label="Unterschrift Vermieter"
        nameValue={ownerName}
        onName={setOwnerName}
        onSignature={setOwnerSig}
        required
      />

      <button
        onClick={handleComplete}
        disabled={!canSubmit}
        className={`w-full py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 transition-all ${
          canSubmit
            ? 'bg-green-600 hover:bg-green-500 active:scale-[0.98] text-white shadow-lg shadow-green-600/30'
            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
        }`}
      >
        {submitting ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Wird gespeichert…</>
        ) : (
          <><CheckCircle className="w-5 h-5" /> Unterzeichnen &amp; Speichern</>
        )}
      </button>

      {(!tenantSig || !ownerSig) && (
        <p className="text-xs text-center text-gray-500">
          Beide Unterschriften erforderlich
        </p>
      )}
    </div>
  );
};

// ── Schritt 4b: Bestätigung + Absenden (für Mietverträge via DocuSeal) ────────
const StepBestaetigung = ({ property, template, formData, onSubmit, submitting, result }) => {
  if (result) {
    return (
      <div className="text-center space-y-6 py-8">
        <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto border-2 border-green-500">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Signaturanfrage gesendet!</h2>
          <p className="text-gray-400 text-sm">
            Beide Parteien erhalten eine E-Mail mit ihrem persönlichen Signaturlink.
          </p>
        </div>
        <div className="bg-gray-700/50 rounded-xl p-4 text-left space-y-2">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Signaturlinks</p>
          {result.submitters?.map((s, i) => (
            <div key={i} className="flex items-center justify-between">
              <span className="text-sm text-gray-300">{s.role || s.name}</span>
              <a
                href={s.sign_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-400 hover:text-blue-300 underline"
              >
                Link öffnen →
              </a>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500">Submission-ID: {result.submission_id}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Zusammenfassung</h2>
        <p className="text-gray-400 text-sm">Bitte prüfen und bestätigen.</p>
      </div>
      <div className="space-y-3">
        {[
          { label: 'Objekt', value: property?.street_address || property?.name },
          { label: 'Dokument', value: template?.label || template?.name },
          { label: 'Mieter', value: formData?.mieter_name },
          { label: 'Mieter E-Mail', value: formData?.mieter_email },
          { label: 'Vermieter', value: formData?.vermieter_name },
          { label: 'Vermieter E-Mail', value: formData?.vermieter_email },
        ].map(({ label, value }) => (
          <div key={label} className="flex items-start justify-between py-2 border-b border-gray-700/50">
            <span className="text-sm text-gray-400 flex-shrink-0 w-32">{label}</span>
            <span className="text-sm text-white text-right">{value || '—'}</span>
          </div>
        ))}
      </div>
      <button
        onClick={onSubmit}
        disabled={submitting || !formData?.mieter_email}
        className={`w-full py-4 rounded-xl font-semibold text-base flex items-center justify-center gap-2 transition-all ${
          submitting || !formData?.mieter_email
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white shadow-lg shadow-blue-600/30'
        }`}
      >
        {submitting ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Wird gesendet…</>
        ) : (
          <><Send className="w-5 h-5" /> Signaturanfrage senden</>
        )}
      </button>
      {!formData?.mieter_email && (
        <p className="text-xs text-center text-yellow-400">
          Bitte E-Mail-Adresse des Mieters angeben (Schritt 3).
        </p>
      )}
    </div>
  );
};

// ── Ergebnis-Screen (Native Canvas) ──────────────────────────────────────────
const CanvasResult = ({ result, onReset }) => (
  <div className="text-center space-y-6 py-8">
    <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto border-2 border-green-500">
      <CheckCircle className="w-10 h-10 text-green-400" />
    </div>
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Dokument unterzeichnet!</h2>
      <p className="text-gray-400 text-sm">
        Das signierte Protokoll wurde im Vault gespeichert und in Supabase eingetragen.
      </p>
    </div>
    <div className="bg-gray-700/50 rounded-xl p-4 text-left space-y-2">
      <p className="text-xs text-gray-400 uppercase tracking-wider">Details</p>
      <div className="flex justify-between">
        <span className="text-sm text-gray-400">Audit-ID</span>
        <span className="text-sm text-white font-mono">{result.audit_id}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-gray-400">Vault Asset</span>
        <span className="text-sm text-white font-mono text-xs">{result.vault_asset_id || '—'}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-gray-400">Status</span>
        <span className="text-sm text-green-400 font-semibold">{result.status}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-gray-400">Zeitstempel</span>
        <span className="text-sm text-white text-xs">
          {new Date(result.signed_at).toLocaleString('de-DE')}
        </span>
      </div>
    </div>
    <button
      onClick={onReset}
      className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-xl text-sm text-gray-300 transition-colors"
    >
      <RefreshCw className="w-4 h-4" /> Neues Protokoll
    </button>
  </div>
);

// ── Haupt-Wizard ──────────────────────────────────────────────────────────────
const STEPS = ['Objekt', 'Dokument', 'Parteien', 'Signatur'];

const TabletWizard = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const [property, setProperty] = useState(null);
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [canvasResult, setCanvasResult] = useState(null);
  const [error, setError] = useState(null);
  const [demoMode, setDemoMode] = useState(false);
  const [demoEmail, setDemoEmail] = useState('torsten@hiss.de');

  const isHandover = template?.category === 'handover';

  useEffect(() => {
    fetch(`${API_BASE}/tenant/config`)
      .then(r => r.json())
      .then(cfg => {
        if (cfg?.demo_mode) { setDemoMode(true); setDemoEmail(cfg.demo_email || 'torsten@hiss.de'); }
      })
      .catch(() => {});
  }, []);

  const canNext = [
    !!property,
    !!template,
    isHandover
      ? !!(formData.mieter_name && formData.vermieter_name)
      : !!(formData.mieter_name && formData.mieter_email),
    true
  ][step];

  // DocuSeal Submit (für Mietverträge)
  const handleDocuSealSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        template_id: template.id,
        template_label: template.label || template.name,
        property_id: property.id,
        submitters: [
          { name: formData.mieter_name, email: formData.mieter_email, role: 'Mieter' },
          ...(formData.vermieter_email ? [{ name: formData.vermieter_name || 'Vermieter', email: formData.vermieter_email, role: 'Vermieter' }] : [])
        ]
      };
      const resp = await fetch(`${API_BASE}/signature/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        if (errData?.demo_mode) throw new Error(`Demo-Modus: Nur ${demoEmail} erlaubt`);
        throw new Error(`Fehler ${resp.status}: ${errData?.message || ''}`);
      }
      const data = await resp.json();
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Native Canvas Submit (für Übergabeprotokolle)
  const handleCanvasComplete = async (sigData) => {
    setSubmitting(true);
    setError(null);
    try {
      // 1. Canvas-Session erstellen (via signature/request → nativeCanvasAdapter)
      const reqResp = await fetch(`${API_BASE}/signature/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: template.id,
          template_label: template.label || template.name,
          property_id: property.id,
          tenant_id: formData.tenant_id || null,
          submitters: [
            { name: formData.mieter_name, email: 'canvas@local', role: 'Mieter' },
            { name: formData.vermieter_name || 'Vermieter', email: 'canvas@local', role: 'Vermieter' }
          ]
        })
      });
      if (!reqResp.ok) {
        const errData = await reqResp.json().catch(() => ({}));
        throw new Error(errData?.error || `Fehler ${reqResp.status}`);
      }
      const reqData = await reqResp.json();
      const canvasToken = reqData.canvas_token;
      if (!canvasToken) throw new Error('Kein Canvas-Token erhalten');

      // 2. Signaturen + PDF → Vault → Supabase
      const completeResp = await fetch(`${API_BASE}/signature/native/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          canvas_token: canvasToken,
          tenant_signature_svg: sigData.tenant_signature_svg,
          owner_signature_svg: sigData.owner_signature_svg,
          signed_by_tenant_name: sigData.signed_by_tenant_name,
          signed_by_owner_name: sigData.signed_by_owner_name,
          signed_at: sigData.signed_at,
          location: sigData.location
        })
      });
      if (!completeResp.ok) {
        const errData = await completeResp.json().catch(() => ({}));
        throw new Error(errData?.error || `Fehler ${completeResp.status}`);
      }
      const completeData = await completeResp.json();
      setCanvasResult(completeData);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0); setProperty(null); setTemplate(null);
    setFormData({}); setResult(null); setCanvasResult(null); setError(null);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PenLine className="w-5 h-5 text-blue-400" />
          <span className="font-semibold text-white text-sm">Vor-Ort-Signatur</span>
          {demoMode && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-600/20 border border-yellow-600/50 rounded-full text-xs text-yellow-400 font-medium">
              <FlaskConical className="w-3 h-3" /> DEMO
            </span>
          )}
          {isHandover && template && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-green-600/20 border border-green-700/50 rounded-full text-xs text-green-400 font-medium">
              <Fingerprint className="w-3 h-3" /> Canvas
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(result || canvasResult) && (
            <button onClick={reset} className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Neu
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="text-xs text-gray-400 hover:text-white px-3 py-1 rounded-lg hover:bg-gray-700">
              ✕ Schließen
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-lg mx-auto w-full">
        {!result && !canvasResult && <StepIndicator steps={STEPS} current={step} />}

        {error && (
          <div className="mb-4 bg-red-900/20 border border-red-700/50 rounded-xl p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {canvasResult && <CanvasResult result={canvasResult} onReset={reset} />}

        {!canvasResult && (
          <>
            {step === 0 && <StepObjekt onSelect={p => setProperty(p)} selected={property} />}
            {step === 1 && <StepTemplate onSelect={t => setTemplate(t)} selected={template} />}
            {step === 2 && (
              <StepDaten
                property={property}
                template={template}
                onData={setFormData}
                initialData={formData}
              />
            )}
            {step === 3 && isHandover && (
              <StepCanvasSignatur
                property={property}
                template={template}
                formData={formData}
                onComplete={handleCanvasComplete}
                submitting={submitting}
              />
            )}
            {step === 3 && !isHandover && (
              <StepBestaetigung
                property={property}
                template={template}
                formData={formData}
                onSubmit={handleDocuSealSubmit}
                submitting={submitting}
                result={result}
              />
            )}
          </>
        )}
      </div>

      {/* Navigation — nur wenn kein Ergebnis und kein Canvas-Screen im letzten Schritt */}
      {!result && !canvasResult && !(step === 3 && isHandover) && (
        <div className="bg-gray-800 border-t border-gray-700 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={step === 0}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium text-sm transition-all ${
              step === 0
                ? 'text-gray-600 cursor-not-allowed'
                : 'text-gray-300 hover:text-white hover:bg-gray-700 active:scale-[0.98]'
            }`}
          >
            <ChevronLeft className="w-4 h-4" /> Zurück
          </button>
          <div className="flex-1" />
          {step < STEPS.length - 1 && (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canNext}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                canNext
                  ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20 active:scale-[0.98]'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              Weiter <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Zurück-Button auf Canvas-Screen */}
      {step === 3 && isHandover && !canvasResult && (
        <div className="bg-gray-800 border-t border-gray-700 px-4 py-3">
          <button
            onClick={() => setStep(2)}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-medium text-sm text-gray-300 hover:text-white hover:bg-gray-700 transition-all"
          >
            <ChevronLeft className="w-4 h-4" /> Zurück
          </button>
        </div>
      )}
    </div>
  );
};

export default TabletWizard;
